import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { DefaultStyleResource } from '../resource/default-style.resource';
import { SetDefaultStyleDto, DefaultStylesDto, ModelDto, ModelElementType } from '@maporium-workspace/shared';

@Injectable({
  providedIn: 'root'
})
export class DefaultStyleService {
  private defaultStylesCache: DefaultStylesDto | null = null;
  private defaultStylesSubject = new BehaviorSubject<DefaultStylesDto | null>(null);
  public defaultStyles$ = this.defaultStylesSubject.asObservable();

  constructor(private resource: DefaultStyleResource) {}

  getUserDefaultStyles(): Observable<DefaultStylesDto> {
    return this.resource.getUserDefaultStyles().pipe(
      tap(styles => {
        this.defaultStylesCache = styles;
        this.defaultStylesSubject.next(styles);
      })
    );
  }

  setDefaultStyle(elementType: ModelElementType, elementId?: string, mapId?: string): Observable<ModelDto> {
    return this.resource.setDefaultStyle(elementType, elementId, mapId).pipe(
      tap(() => {
        // Refresh cache after setting new default
        this.getUserDefaultStyles().subscribe();
      })
    );
  }

  resetDefaultStyles(): Observable<{ message: string }> {
    return this.resource.resetDefaultStyles().pipe(
      tap(() => {
        // Clear cache after reset
        this.defaultStylesCache = null;
        this.defaultStylesSubject.next(null);
      })
    );
  }

  getCachedDefaultStyles(): DefaultStylesDto | null {
    return this.defaultStylesCache;
  }

  hasDefaultStyle(elementType: ModelElementType): boolean {
    if (!this.defaultStylesCache) return false;

    switch (elementType) {
      case ModelElementType.NODE:
        return !!this.defaultStylesCache.nodeDefault;
      case ModelElementType.LINK:
        return !!this.defaultStylesCache.linkDefault;
      case ModelElementType.SMAP:
        return !!this.defaultStylesCache.smapDefault;
      default:
        return false;
    }
  }

  // Initialize defaults on app startup
  loadDefaultStyles(): void {
    this.getUserDefaultStyles().subscribe();
  }
}